import type { ReactNode } from "react";
import { AlertTriangle } from "./icons";
import { Button } from "./primitives";

/**
 * A speed bump for an action that is hard to undo but carries no reason worth recording — unlike
 * `ReasonDialog`, which exists precisely because a reason it captures gets read again months later
 * (constitution VI). Sending every customer a statement, or replacing the whole permission matrix,
 * has nothing to log beyond "someone clicked it" — this is that "someone clicked it, on purpose."
 *
 * Same native `<dialog>` + real `<form>` shape as `ReasonDialog`, minus the mandatory textarea, so
 * the two read as one family rather than two unrelated confirmation patterns.
 */
export function ConfirmDialog({
  title,
  consequence,
  action,
  confirmLabel,
  cancel,
  error,
  open = true,
  children,
}: {
  title: string;
  /** What this does and what it cannot undo. Required on purpose. */
  consequence: ReactNode;
  /** The Server Action, or a route. This component performs no mutation of its own. */
  action: React.ComponentProps<"form">["action"];
  /** Names the act — "Send statements", not "OK". */
  confirmLabel: string;
  /** The way out. A confirmation with no exit is a trap. */
  cancel: ReactNode;
  /** The API's rejection, rendered verbatim (constitution VI). */
  error?: string;
  open?: boolean;
  /** Anything else the decision needs — the affected count, the rows involved. */
  children?: ReactNode;
}) {
  return (
    <dialog
      open={open}
      data-slot="confirm-dialog"
      aria-labelledby="confirm-dialog-title"
      className="border-muted bg-gray-0 text-foreground shadow-lg z-50 w-full max-w-lg rounded-xl border p-0"
    >
      <form action={action} noValidate className="flex flex-col gap-4 p-5">
        <div className="flex gap-3.5">
          <span className="bg-red/10 text-red-dark mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg">
            <AlertTriangle aria-hidden className="size-4" />
          </span>
          <div className="flex min-w-0 flex-col gap-1.5">
            <h2 id="confirm-dialog-title" className="text-base font-semibold">
              {title}
            </h2>
            <p className="text-muted-foreground text-sm">{consequence}</p>
          </div>
        </div>

        {children}

        {error ? (
          <p role="alert" className="mt-0.5 text-[13px] text-red">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          {cancel}
          <Button type="submit" variant="destructive" size="lg">
            {confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}
